import React from 'react';
import { Loader2, ArrowDown } from 'lucide-react';
import { usePullToRefresh, PULL_THRESHOLD } from './usePullToRefresh';

/**
 * A scroll container that refreshes when pulled down.
 *
 * Drop-in replacement for the `min-h-0 flex-1 overflow-y-auto` div every phone list
 * already scrolls in — pass the same className and wrap the same children:
 *
 *   <PullToRefresh onRefresh={load} className="min-h-0 flex-1 overflow-y-auto">
 *       …list…
 *   </PullToRefresh>
 *
 * The phone build has no refresh control at all: the tab bar switches views and each
 * list loads once on mount, so a stale list could only be fixed by killing the app —
 * which in an installed PWA is neither obvious nor discoverable.
 *
 * ⚠️ `overscroll-behavior-y: contain` is REQUIRED, not polish. Without it iOS runs its
 * own rubber-band at the same time, so the user sees the platform bounce AND this
 * indicator fighting each other, and on an installed PWA the gesture can escape to the
 * browser's own page-refresh. It also stops a pull at the top of a list scrolling the
 * page behind it.
 *
 * @param {() => (void|Promise<void>)} onRefresh  usually the screen's existing `load`
 * @param {boolean} [disabled]  suppress the gesture, e.g. while a sheet is open
 */
const PullToRefresh = ({ onRefresh, disabled = false, className = '', children, ...rest }) => {
    const { containerRef, pullDistance, refreshing, armedToRefresh } =
        usePullToRefresh(onRefresh, { disabled });

    const active = pullDistance > 0 || refreshing;
    // While refreshing, hold the indicator open at the threshold so it does not snap
    // shut the instant the finger lifts and then reappear.
    const offset = refreshing ? PULL_THRESHOLD : pullDistance;

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ overscrollBehaviorY: 'contain' }}
            {...rest}
        >
            {/* The indicator occupies real height rather than overlaying, so the list is
                pushed down and the gesture reads as moving the content — an overlay
                looks like a floating spinner unrelated to the pull. */}
            <div
                className="flex items-center justify-center overflow-hidden"
                style={{
                    height: `${offset}px`,
                    // No transition mid-drag: the height must track the finger exactly.
                    // Animate only on release, and not at all under reduced motion.
                    transition: pullDistance > 0 ? 'none' : 'height 200ms ease-out',
                }}
                aria-hidden={!active}
            >
                {active && (
                    <span
                        className="flex items-center gap-1.5 text-xs font-medium text-emerald-700"
                        role="status"
                        aria-live="polite"
                    >
                        {refreshing ? (
                            <>
                                <Loader2 size={15} className="animate-spin" />
                                Refreshing…
                            </>
                        ) : (
                            <>
                                <ArrowDown
                                    size={15}
                                    className="motion-safe:transition-transform"
                                    style={{ transform: armedToRefresh ? 'rotate(180deg)' : 'none' }}
                                />
                                {armedToRefresh ? 'Release to refresh' : 'Pull to refresh'}
                            </>
                        )}
                    </span>
                )}
            </div>

            {children}
        </div>
    );
};

export default PullToRefresh;
